import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout, Button } from '../common/index.js';
import DateFilterDropdown from './DateFilterDropdown.js';
import analyticsService from '../../services/AnalyticsService';
import { Box, ArrowLeft, TrendingDown, AlertTriangle, Package, Users, Truck, DollarSign } from 'lucide-react';

const InventoryAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('current_month');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = filter === 'custom' ? { filter, ...customDates } : { filter };
      const response = await analyticsService.getInventoryAnalytics(params);
      setData(response);
    } catch (error) {
      console.error('Error fetching inventory analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <h3 className="text-xl lg:text-2xl font-bold text-gray-800">{value}</h3>
          {subtitle && <p className="text-xs lg:text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 lg:p-3 rounded-full ${color}`}>
          <Icon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <PageLayout title="Inventory Analytics" icon={Box}>
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
            <p className="text-secondary">Loading inventory analytics...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Inventory Analytics"
      icon={Box}
      actions={
        <>
          <DateFilterDropdown filter={filter} setFilter={setFilter} customDates={customDates} setCustomDates={setCustomDates} onApply={fetchData} dateRange={data?.filter} />
          <Button variant="secondary" size="sm" icon={ArrowLeft} onClick={() => navigate('/analytics')}>Back</Button>
        </>
      }
    >
      <p className="text-sm text-secondary -mt-2 mb-6">Stock management and usage tracking</p>

                {data && (
                  <>
                    {/* Inventory Overview */}
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                      <StatCard
                        title="Total Items"
                        value={data.inventory_overview.total_items}
                        subtitle="In inventory"
                        icon={Box}
                        color="bg-brand"
                      />
                      <StatCard
                        title="Total Value"
                        value={formatCurrency(data.inventory_overview.total_inventory_value.total_value)}
                        subtitle="All inventory"
                        icon={DollarSign}
                        color="bg-green-500"
                      />
                      <StatCard
                        title="Out of Stock"
                        value={data.inventory_overview.out_of_stock_items}
                        subtitle="Need restocking"
                        icon={AlertTriangle}
                        color="bg-red-500"
                      />
                      <StatCard
                        title="Total Quantity"
                        value={data.inventory_overview.total_inventory_value.total_quantity}
                        subtitle="Units in stock"
                        icon={Package}
                        color="bg-purple-500"
                      />
                    </div>

                    {/* Status & Category Breakdown */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      {/* By Status */}
                      <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Inventory by Status</h3>
                        <div className="space-y-3">
                          {Object.entries(data.inventory_overview.by_status).map(([status, stats]) => (
                            <div key={status} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                              <span className="text-sm text-gray-700 capitalize font-medium">{status.replace(/_/g, ' ')}</span>
                              <div className="text-right">
                                <p className="text-sm font-semibold text-gray-800">{stats.count} items</p>
                                <p className="text-xs text-gray-600">{formatCurrency(stats.total_value)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* By Category */}
                      <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Inventory by Category</h3>
                        <div className="space-y-3">
                          {Object.entries(data.inventory_overview.by_category).map(([category, stats]) => (
                            <div key={category} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                              <span className="text-sm text-gray-700 capitalize font-medium">{category.replace(/_/g, ' ')}</span>
                              <div className="text-right">
                                <p className="text-sm font-semibold text-gray-800">{stats.count} items</p>
                                <p className="text-xs text-gray-600">{stats.quantity} units • {formatCurrency(stats.total_value)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Alerts Section */}
                    <div className="bg-white rounded-card shadow-card p-4 lg:p-6 mb-6">
                      <div className="flex items-center mb-4">
                        <AlertTriangle className="w-6 h-6 text-blue-500 mr-2" />
                        <h3 className="text-lg font-semibold text-gray-800">Inventory Alerts</h3>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                        <div className="p-4 bg-red-50 rounded">
                          <p className="text-sm text-gray-600 mb-1">Low Stock Items</p>
                          <p className="text-2xl font-bold text-red-600">{data.alerts.low_stock_items_count}</p>
                        </div>
                        <div className="p-4 bg-blue-50 rounded">
                          <p className="text-sm text-gray-600 mb-1">Near Expiry (30 days)</p>
                          <p className="text-2xl font-bold text-blue-600">{data.alerts.near_expiry_items}</p>
                        </div>
                        <div className="p-4 bg-yellow-50 rounded">
                          <p className="text-sm text-gray-600 mb-1">Expired Items</p>
                          <p className="text-2xl font-bold text-yellow-600">{data.alerts.expired_items}</p>
                        </div>
                      </div>

                      {data.alerts.low_stock_items && data.alerts.low_stock_items.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Items Requiring Attention:</h4>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                            {data.alerts.low_stock_items.slice(0, 6).map((item) => (
                              <div key={item.inventory_id} className="flex items-center justify-between p-2 bg-red-50 rounded text-xs">
                                <span className="text-gray-700 font-medium truncate mr-2">{item.item_name}</span>
                                <span className="text-red-600 font-bold">{item.quantity} {item.unit}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Usage Analytics */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                      <StatCard
                        title="Total Usage Records"
                        value={data.usage_analytics.summary.total_usage_records}
                        subtitle="In selected period"
                        icon={TrendingDown}
                        color="bg-indigo-500"
                      />
                      <StatCard
                        title="Total Usage Cost"
                        value={formatCurrency(data.usage_analytics.summary.total_cost)}
                        subtitle="Materials consumed"
                        icon={DollarSign}
                        color="bg-rose-500"
                      />
                      <StatCard
                        title="Quantity Used"
                        value={data.usage_analytics.summary.total_quantity_used}
                        subtitle="Total units"
                        icon={Package}
                        color="bg-teal-500"
                      />
                    </div>

                    {/* Usage Breakdown */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      {/* By Usage Type */}
                      <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Usage by Type</h3>
                        <div className="space-y-3">
                          {Object.entries(data.usage_analytics.by_usage_type).map(([type, stats]) => (
                            <div key={type} className="flex items-center justify-between p-3 bg-brand-light rounded">
                              <span className="text-sm text-gray-700 capitalize font-medium">{type.replace(/_/g, ' ')}</span>
                              <div className="text-right">
                                <p className="text-sm font-semibold text-brand">{formatCurrency(stats.total_cost)}</p>
                                <p className="text-xs text-gray-600">{stats.count} records • {stats.quantity_used} units</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* By Usage Purpose */}
                      <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Usage by Purpose</h3>
                        <div className="space-y-3">
                          {Object.entries(data.usage_analytics.by_usage_purpose).map(([purpose, stats]) => (
                            <div key={purpose} className="flex items-center justify-between p-3 bg-green-50 rounded">
                              <span className="text-sm text-gray-700 capitalize font-medium">{purpose.replace(/_/g, ' ')}</span>
                              <div className="text-right">
                                <p className="text-sm font-semibold text-green-600">{formatCurrency(stats.total_cost)}</p>
                                <p className="text-xs text-gray-600">{stats.count} records • {stats.quantity_used} units</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Top Used Items */}
                    {data.usage_analytics.top_used_items && data.usage_analytics.top_used_items.length > 0 && (
                      <div className="bg-white rounded-card shadow-card p-4 lg:p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Used Items</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity Used</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Usage Count</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {data.usage_analytics.top_used_items.map((item) => (
                                <tr key={item.inventory_id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.item_name}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600 capitalize">{item.category.replace(/_/g, ' ')}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-900">{item.total_quantity_used}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-600">{item.usage_count}</td>
                                  <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">{formatCurrency(item.total_cost)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Usage by Department, Vehicle, and User */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                      {/* By Department */}
                      {data.usage_analytics.by_department && data.usage_analytics.by_department.length > 0 && (
                        <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
                          <div className="flex items-center mb-4">
                            <Users className="w-5 h-5 text-purple-500 mr-2" />
                            <h3 className="text-lg font-semibold text-gray-800">By Department</h3>
                          </div>
                          <div className="space-y-2">
                            {data.usage_analytics.by_department.map((dept) => (
                              <div key={dept.department} className="flex items-center justify-between p-2 bg-purple-50 rounded">
                                <span className="text-sm text-gray-700 font-medium">{dept.department}</span>
                                <div className="text-right">
                                  <p className="text-sm font-semibold text-purple-600">{formatCurrency(dept.total_cost)}</p>
                                  <p className="text-xs text-gray-600">{dept.usage_count} uses</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* By Vehicle */}
                      {data.usage_analytics.by_vehicle && data.usage_analytics.by_vehicle.length > 0 && (
                        <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
                          <div className="flex items-center mb-4">
                            <Truck className="w-5 h-5 text-yellow-500 mr-2" />
                            <h3 className="text-lg font-semibold text-gray-800">By Vehicle</h3>
                          </div>
                          <div className="space-y-2">
                            {data.usage_analytics.by_vehicle.slice(0, 5).map((vehicle) => (
                              <div key={vehicle.vehicle_id} className="flex items-center justify-between p-2 bg-yellow-50 rounded">
                                <span className="text-sm text-gray-700 font-medium">{vehicle.vehicle_number}</span>
                                <div className="text-right">
                                  <p className="text-sm font-semibold text-yellow-600">{formatCurrency(vehicle.total_cost)}</p>
                                  <p className="text-xs text-gray-600">{vehicle.usage_count} uses</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* By User */}
                      {data.usage_analytics.by_user && data.usage_analytics.by_user.length > 0 && (
                        <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
                          <div className="flex items-center mb-4">
                            <Users className="w-5 h-5 text-indigo-500 mr-2" />
                            <h3 className="text-lg font-semibold text-gray-800">By User</h3>
                          </div>
                          <div className="space-y-2">
                            {data.usage_analytics.by_user.slice(0, 5).map((user) => (
                              <div key={user.user_id || user.user_name} className="flex items-center justify-between p-2 bg-indigo-50 rounded">
                                <span className="text-sm text-gray-700 font-medium">{user.user_name}</span>
                                <div className="text-right">
                                  <p className="text-sm font-semibold text-indigo-600">{formatCurrency(user.total_cost)}</p>
                                  <p className="text-xs text-gray-600">{user.usage_count} uses</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Warehouse Breakdown */}
                    {data.inventory_overview.by_warehouse && data.inventory_overview.by_warehouse.length > 0 && (
                      <div className="bg-white rounded-card shadow-card p-4 lg:p-6 mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Inventory by Warehouse</h3>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          {data.inventory_overview.by_warehouse.map((warehouse) => (
                            <div key={warehouse.warehouse} className="p-4 bg-gray-50 rounded">
                              <h4 className="font-semibold text-gray-800 mb-2">{warehouse.warehouse}</h4>
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Items:</span>
                                  <span className="font-medium text-gray-800">{warehouse.item_count}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Total Quantity:</span>
                                  <span className="font-medium text-gray-800">{warehouse.total_quantity} units</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Total Value:</span>
                                  <span className="font-semibold text-green-600">{formatCurrency(warehouse.total_value)}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Monthly Usage Trend */}
                    {data.monthly_usage_trend && data.monthly_usage_trend.length > 0 && (
                      <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Monthly Usage Trend</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Usage Count</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {data.monthly_usage_trend.map((trend) => (
                                <tr key={trend.month} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{trend.month}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-900">{trend.usage_count}</td>
                                  <td className="px-4 py-3 text-sm text-right font-semibold text-green-600">{formatCurrency(trend.total_cost)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
    </PageLayout>
  );
};

export default InventoryAnalytics;